import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { DEFAULT_UI_SCALE } from './ui-scale';

/*
 * UI-tila Zustandilla.
 *
 * Erottelu kahteen kerrokseen:
 *
 * 1. Persistoitu (localStorage, avain "luviamo-ui-prefs"):
 *    - sidebarOpen: sivupalkin auki/kiinni-tila
 *    - theme: 'light' | 'dark' (dark-tuki tulee myohemmin, nyt vain light)
 *    - locale: 'fi' | 'en' (i18n-kieli, kytketaan react-i18nextiin Sessio 3.5b:ssa)
 *    - uiScale: UI-skaalaus 1 / 1.1 / 1.25 / 1.4 (Sessio 21.5, ks. lib/ui-scale.ts)
 *
 * 2. Transientti (in-memory, ei tallenneta):
 *    - assistantOpen: Avustaja-paneelin tila (cmd+K, SEED 12.8)
 *    - activeModal: avoimen modaalin tunniste tai null
 *
 * Auth-token (Sessio 5:ssa) ei tule taman storen kentaksi vaan se asetetaan
 * lib/api.ts:n setAccessToken-funktion kautta in-memoryyn. Tama estaa tokenin
 * vuotamisen localStorageen XSS-syiden takia, ja pitaa storen huomion
 * pelkasti UI-tilassa.
 */

type Theme = 'light' | 'dark';
type Locale = 'fi' | 'en';
export type LibraryView = 'grid' | 'list';
export type LibraryTab = 'images' | 'templates' | 'snippets';

type UIPersistedState = {
  sidebarOpen: boolean;
  theme: Theme;
  locale: Locale;
  /** UI-skaalaus (Sessio 21.5): 1 / 1.1 / 1.25 / 1.4. Ks. lib/ui-scale.ts. */
  uiScale: number;
  /** Näytetäänkö toimintovihjeet (Osa 3 vihjejärjestelmä). Oletus true. */
  showHints: boolean;
  /** Kirjaston näkymä (Osa B5): ruudukko vai lista. Oletus grid. */
  libraryView: LibraryView;
  /** Kirjaston aktiivinen välilehti (Osa C2): kuvat / pohjat / katkelmat. Oletus images. */
  libraryTab: LibraryTab;
  /** Tuota-lista: näytetäänkö julkaistut postaukset (Joanna 4.9.2026). Oletus false = piilossa. */
  tuotaShowPublished: boolean;
  /** Suunnittele: "Valmiit – odottavat ajoitusta" -paneeli pienennettynä (Joanna 4.9.2026). */
  planningPanelCollapsed: boolean;
  /** SEURAA Parhaat postaukset: valitut lisäsarakkeet muistetaan (Joanna 4.9.2026). */
  topPostsColumns: string[];
  /** Strategiasuunnitelman lohkot suljettuina (Petri 8.9.2026): avain = lohkon id, true = suljettu. */
  planBlocksCollapsed: Record<string, boolean>;
};

type UITransientState = {
  assistantOpen: boolean;
  activeModal: string | null;
  /**
   * Mobile-drawer:n tila (Sessio 4b, C4-paatos). Erillinen sidebarOpen:ista koska
   * mobiilissa drawer on oletuksena suljettu eika asetus seuraa kayttajan
   * desktop-preferenssia. Reset:tetaan jokaisella sivunavauksella.
   */
  sidebarMobileOpen: boolean;
};

type UIActions = {
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  setTheme: (theme: Theme) => void;
  setLocale: (locale: Locale) => void;
  setUiScale: (uiScale: number) => void;
  setShowHints: (showHints: boolean) => void;
  setLibraryView: (libraryView: LibraryView) => void;
  setLibraryTab: (libraryTab: LibraryTab) => void;
  setTuotaShowPublished: (show: boolean) => void;
  setPlanningPanelCollapsed: (collapsed: boolean) => void;
  setTopPostsColumns: (columns: string[]) => void;
  setPlanBlockCollapsed: (id: string, collapsed: boolean) => void;
  setAssistantOpen: (open: boolean) => void;
  setActiveModal: (modal: string | null) => void;
  setSidebarMobileOpen: (open: boolean) => void;
  toggleSidebarMobile: () => void;
};

type UIStore = UIPersistedState & UITransientState & UIActions;

export const useUIStore = create<UIStore>()(
  persist(
    (set) => ({
      // Persistoidut kentat
      sidebarOpen: true,
      theme: 'light',
      locale: 'fi',
      uiScale: DEFAULT_UI_SCALE,
      showHints: true,
      libraryView: 'grid',
      libraryTab: 'images',
      tuotaShowPublished: false,
      planningPanelCollapsed: false,
      topPostsColumns: [],
      planBlocksCollapsed: {},

      // Transientit kentat
      assistantOpen: false,
      activeModal: null,
      sidebarMobileOpen: false,

      // Toiminnot
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
      setTheme: (theme) => set({ theme }),
      setLocale: (locale) => set({ locale }),
      setUiScale: (uiScale) => set({ uiScale }),
      setShowHints: (showHints) => set({ showHints }),
      setLibraryView: (libraryView) => set({ libraryView }),
      setLibraryTab: (libraryTab) => set({ libraryTab }),
      setTuotaShowPublished: (tuotaShowPublished) => set({ tuotaShowPublished }),
      setPlanningPanelCollapsed: (planningPanelCollapsed) => set({ planningPanelCollapsed }),
      setTopPostsColumns: (topPostsColumns) => set({ topPostsColumns }),
      setPlanBlockCollapsed: (id, collapsed) => set((s) => ({ planBlocksCollapsed: { ...s.planBlocksCollapsed, [id]: collapsed } })),
      setAssistantOpen: (open) => set({ assistantOpen: open }),
      setActiveModal: (modal) => set({ activeModal: modal }),
      setSidebarMobileOpen: (open) => set({ sidebarMobileOpen: open }),
      toggleSidebarMobile: () => set((s) => ({ sidebarMobileOpen: !s.sidebarMobileOpen })),
    }),
    {
      name: 'luviamo-ui-prefs',
      storage: createJSONStorage(() => localStorage),
      version: 1,
      // Vain kayttajapreferenssit talteen, transientti tila ei. Puuttuva kentta (vanha
      // tallennus ennen kentan lisaysta) saa shallow-mergessa initial-arvon (esim. uiScale=1,
      // showHints=true) -> ei version-bumppia tarvita.
      partialize: (state) => ({
        sidebarOpen: state.sidebarOpen,
        theme: state.theme,
        locale: state.locale,
        uiScale: state.uiScale,
        showHints: state.showHints,
        libraryView: state.libraryView,
        libraryTab: state.libraryTab,
        tuotaShowPublished: state.tuotaShowPublished,
        planningPanelCollapsed: state.planningPanelCollapsed,
        topPostsColumns: state.topPostsColumns,
        planBlocksCollapsed: state.planBlocksCollapsed,
      }),
    },
  ),
);
